import { NativeSelect, Select, TextInput } from '@common/components';
import { ChangeEvent, FC, Dispatch, SetStateAction, Fragment, useEffect, useState } from 'react';
import { statusMap, searchTypeMap, widgetSettingsMap, issueTypeMap } from 'src/constants/statuses'
import { periodMap } from 'src/constants/periods';
import { capitalizeFirstLetter } from 'src/helpers/capitalizeFirstLetter';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store';
import { shallowEqual } from 'react-redux';
import { getDateRange } from 'src/helpers/getDateRange';

import styles from './styles.module.scss';
import { useDisclosure, useMediaQuery, useOs } from '@mantine/hooks';
import { Drawer, Flex, Title, UnstyledButton } from '@mantine/core';
import classNames from 'classnames';
import { Sliders, X } from '@geist-ui/icons';
import i18n from '@common/i18n/config';

interface FilterMyReportsProps {
  searchQuery: { [key: string]: string | null };
  setSearchQuery: Dispatch<SetStateAction<{ [key: string]: string | null }>>;
  setDefault: () => void;
  onIsActive: (item: boolean) => void;
}

const FilterMyReports: FC<FilterMyReportsProps> = ({ 
  searchQuery,
  setSearchQuery,
  onIsActive }) => {
  const os = useOs();
  let searchTypePlaceholder:string = 'searchReportBy';
  const [status, setStatus] = useState<string | null>('all');
  const [period, setPeriod] = useState<string | null>('all');
  const [searchType, setSearchType] = useState<string | null>('all');
  const [settings, setWidgetSettings] = useState<string | null>('');
  const [issueType, setIssueType] = useState<string | null>('');
  const [isActive, setIsActive] = useState<boolean>(false);
  const [t] = useTranslation('reported');
  const [c] = useTranslation('common');
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const myReports = useAppSelector((state) => state.myReports, shallowEqual);
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const isMediumDevice = useMediaQuery('(max-width: 992px)');
  const [statusMapData, setStatusMapData] = useState(statusMap);
  const [periodMapData, setPeriodMapData] = useState(periodMap);
  const [searchTypeMapData, setSearchTypeMapData] = useState(searchTypeMap);
  const [widgetSettingsMapData, setWidgetSettingsMapData] = useState(widgetSettingsMap);
  const [issueTypesMapData, setIssueTypesMapData] = useState(issueTypeMap);

  const handleNativeStatusChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setStatus(e.currentTarget.value);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    handleChange('status', e.currentTarget.value);
  };

  const handleNativePeriodChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setPeriod(e.currentTarget.value);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    handleChange('period', e.currentTarget.value);
  };

  const handleNativeWidgetSettingsChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setWidgetSettings(e.currentTarget.value)
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    handleChange('settings', e.currentTarget.value);
  };

  const handleNativeIssueTyeChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setIssueType(e.currentTarget.value);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    handleChange('issueType', e.currentTarget.value);
  };

  const handleNativeSearchTypeChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setSearchType(e.currentTarget.value);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    handleChange('searchType', e.currentTarget.value);
  };

  const handleStatusChange = (field: string, value: string | null): void => {
    setStatus(value);
    handleChange(field, value);
  }; 

  const handlePeriodChange = (field: string, value: string | null): void => {
    setPeriod(value);
    handleChange(field, value);
  }; 

  const handleChange = (field: string, value: string | null): void => {
    setIsActive(true);
    if (field === 'period') {
      const { dateFrom, dateTo } = getDateRange(value);
      searchQuery.dateFrom = dateFrom.toString();
      searchQuery.dateTo = dateTo.toString();
    }
    if (field === 'searchType') {
      setSearchQuery((prevState) => ({ ...prevState, ['issueType']: '' }));
      setSearchQuery((prevState) => ({ ...prevState, ['settings']: '' }));
      setSearchQuery((prevState) => ({ ...prevState, ['searchString']: '' }));
    }
    searchQuery.status = searchQuery.status === 'all' ? '' : searchQuery.status;
    setSearchQuery((prevState) => ({ ...prevState, [field]: value }));
  };

  const handlePlaceholder = (field: string| null): any => {
    if (field) {
      searchTypePlaceholder = searchTypePlaceholder + capitalizeFirstLetter(field);
    }
    return t(searchTypePlaceholder)
  };


  useEffect(() => {
    onIsActive(isActive);
  }, [isActive]);

  useEffect(() => {
    myReports;
  }, [myReports]);

  useEffect(() => {
    const setValues = (): void => {
      const newStatusMap = statusMap.map((e) => ({ ...e, label: t(e.label) }));
      const newPeriodMap = periodMap.map((e) => ({ ...e, label: t(e.label) }));
      const newSearchTypeMap = searchTypeMap.map((e) => ({ ...e, label: t(e.label) }));
      const newWidgetSettingsMap = widgetSettingsMap.map((e) => ({ ...e, label: t(e.label) }));
      const newIssueTypesMap = issueTypeMap.map((e) => ({ ...e, label: t(e.label) }));

      setStatusMapData(newStatusMap);
      setPeriodMapData(newPeriodMap);
      setSearchTypeMapData(newSearchTypeMap);
      setWidgetSettingsMapData(newWidgetSettingsMap);
      setIssueTypesMapData(newIssueTypesMap);
    };

    setValues();

    i18n.on('languageChanged', setValues);

    return () => {
      i18n.off('languageChanged', setValues);
    };
  }, []);

  return (
    <div className={styles['filters-list']}>
      {!isMediumDevice ? (
        <Fragment>
           {os === 'ios' || os === 'macos' ? (
            <>
            <NativeSelect
                className={styles.nativeSelect}
                variant="secondary"
                aria-label={`${c('combobox')}`}
                value={searchQuery.searchType as string}
                data={searchTypeMapData}
                onChange={handleNativeSearchTypeChange}
              />
            {searchQuery.searchType === 'settings' ? (
            <NativeSelect
              className={styles.nativeSelect}
              variant="secondary"
              aria-label={`${c('combobox')}`}
              value={searchQuery.settings as string}
              data={widgetSettingsMapData}
              onChange={handleNativeWidgetSettingsChange}
            />)
            : searchQuery.searchType === 'type' ? 
            (
              <NativeSelect
                className={styles.nativeSelect}
                variant="secondary"
                aria-label={`${c('combobox')}`}
                value={searchQuery.issueType as string}
                data={issueTypesMapData}
                onChange={handleNativeIssueTyeChange}
              />)
            : (
              <>
                <TextInput
                  className={styles.search}
                  label={`${t('searchReportBy')}`}
                  ariaLabel={`${t('searchReportBy')}`}
                  placeholder={handlePlaceholder(searchQuery.searchType)}
                  value={searchQuery.searchString as string}
                  onChange={(e) => handleChange('searchString', e.target.value)}
                />
                </>
              )}
              <NativeSelect
                variant="secondary"
                className={styles.nativeSelect}
                aria-label={`${c('combobox')}`}
                value={status as string}
                data={statusMapData}
                onChange={handleNativeStatusChange}
              />
              <NativeSelect
                variant="secondary"
                className={styles.nativeSelect}
                aria-label={`${c('combobox')}`}
                value={period as string}
                data={periodMapData}
                onChange={handleNativePeriodChange}
              />
            </>
          ) : (
            <>
              <Select
                className={styles.searchtype}
                variant="secondary"
                withIcon
                aria-label={`${c('combobox')}`}
                value={searchQuery.searchType}
                data={searchTypeMapData}
                onChange={(e) => handleChange('searchType', e)}
              />
            {searchQuery.searchType === 'settings' ? (
            <Select
              variant="secondary"
              aria-label={`${c('combobox')}`}
              withIcon
              value={searchQuery.settings}
              data={widgetSettingsMapData}
              onChange={(e) => handleChange('settings', e)}
            />)
            : searchQuery.searchType === 'type' ? 
            (
              <Select
                variant="secondary"
                aria-label={`${c('combobox')}`}
                withIcon
                value={searchQuery.issueType}
                data={issueTypesMapData}
                onChange={(e) => handleChange('issueType', e)}
              />)
            : (
              <>
              <TextInput
                className={styles.search}
                label={`${t('searchReportBy')}`}
                ariaLabel={`${t('searchReportBy')}`}
                placeholder={handlePlaceholder(searchQuery.searchType)}
                value={searchQuery.searchString as string}
                onChange={(e) => handleChange('searchString', e.target.value)}
              />
              </>
          )}
              <Select
                variant="secondary"
                aria-label={`${c('combobox')}`}
                withIcon
                value={status}
                data={statusMapData}
                onChange={(e) => handleStatusChange('status', e)}
              />
              <Select
                variant="secondary"
                aria-label={`${c('combobox')}`}
                withIcon
                value={period}
                data={periodMapData}
                onChange={(e) => handlePeriodChange('period', e)}
              />
            </>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <Flex
            w={'100%'}
            className={classNames(styles['filters-container'])}
            py={'8px'}
            mb={'16px'}
            direction={'column'}>
            <Flex justify={'flex-end'} mb={'10px'}>
              <UnstyledButton
                style={{ transform: 'rotate(270deg)' }}
                ml={'16px'}
                className={classNames(styles['action-btn'])}
                aria-label={`${t('openSettings')}`}
                onClick={() => {
                  toggleDrawer();
                }}>
                <Sliders />
              </UnstyledButton>
            </Flex>
            {os === 'ios' || os === 'macos' ? (
                <>
                  <NativeSelect
                      className={styles.nativeSelect}
                      variant="secondary"
                      aria-label={`${c('combobox')}`}
                      value={searchQuery.searchType as string}
                      data={searchTypeMapData}
                      onChange={handleNativeSearchTypeChange}
                  />
                  {searchQuery.searchType === 'settings' ? (
                    <NativeSelect
                      className={styles.nativeSelect}
                      variant="secondary"
                      aria-label={`${c('combobox')}`}
                      value={searchQuery.settings as string}
                      data={widgetSettingsMapData}
                      onChange={handleNativeWidgetSettingsChange}
                    />)
                    : searchQuery.searchType === 'type' ?
                      (
                        <NativeSelect
                          className={styles.nativeSelect}
                          variant="secondary"
                          aria-label={`${c('combobox')}`}
                          value={searchQuery.issueType as string}
                          data={issueTypesMapData}
                          onChange={handleNativeIssueTyeChange}
                        />)
                      : (
                        <>
                          <TextInput
                            className={styles.search}
                            label={`${t('searchReportBy')}`}
                            ariaLabel={`${t('searchReportBy')}`}
                            placeholder={handlePlaceholder(searchQuery.searchType)}
                            value={searchQuery.searchString as string}
                            onChange={(e) => handleChange('searchString', e.target.value)}
                          />
                        </>
                      )}
                </>
              ) : (
                <>
                  <Select
                      className={styles.searchtype}
                      variant="secondary"
                      withIcon
                      aria-label={`${c('combobox')}`}
                      value={searchQuery.searchType}
                      data={searchTypeMapData}
                      onChange={(e) => handleChange('searchType', e)}
                  />
                  {searchQuery.searchType === 'settings' ? (
                    <Select
                      variant="secondary"
                      aria-label={`${c('combobox')}`}
                      withIcon
                      value={searchQuery.settings}
                      data={widgetSettingsMapData}
                      onChange={(e) => handleChange('settings', e)}
                    />)
                    : searchQuery.searchType === 'type' ?
                      (
                        <Select
                          variant="secondary"
                          aria-label={`${c('combobox')}`}
                          withIcon
                          value={searchQuery.issueType}
                          data={issueTypesMapData}
                          onChange={(e) => handleChange('issueType', e)}
                        />)
                      : (
                        <>
                          <TextInput
                            className={styles.search}
                            label={`${t('searchReportBy')}`}
                            ariaLabel={`${t('searchReportBy')}`}
                            placeholder={handlePlaceholder(searchQuery.searchType)}
                            value={searchQuery.searchString as string}
                            onChange={(e) => handleChange('searchString', e.target.value)}
                          />
                        </>
                      )}
                </>
              )}
          </Flex>
          <Drawer
            zIndex={1001}
            size={'100%'}
            className={styles.innerWrapperMobile}
            opened={drawerOpened}
            onClose={closeDrawer}
            withCloseButton={false}>
            <Flex mb={'16px'} justify={'space-between'} align={'center'}>
              <Title className={styles['second-heading']} mb={'8px'} order={2}>
                Filters
              </Title>
              <UnstyledButton
                ml={'16px'}
                className={classNames(styles['action-btn'])}
                aria-label={`${t('cancelSettings')}`}
                onClick={() => {
                  toggleDrawer();
                }}>
                <X />
              </UnstyledButton>
            </Flex>

            <Flex gap={'14px'} direction={'column'}>
            {os === 'ios' || os === 'macos' ? (
                  <>
                    <NativeSelect
                      variant="secondary"
                      className={styles.nativeSelect}
                      aria-label={`${c('combobox')}`}
                      value={status as string}
                      data={statusMapData}
                      onChange={handleNativeStatusChange}
                    />
                    <NativeSelect
                      variant="secondary"
                      className={styles.nativeSelect}
                      aria-label={`${c('combobox')}`}
                      value={period as string}
                      data={periodMapData}
                      onChange={handleNativePeriodChange}
                    />
                  </>
                ) : (
                  <>
                    <Select
                      variant="secondary"
                      aria-label={`${c('combobox')}`}
                      withIcon
                      value={status}
                      data={statusMapData}
                      onChange={(e) => handleStatusChange('status', e)}
                    />
                    <Select
                      variant="secondary"
                      aria-label={`${c('combobox')}`}
                      withIcon
                      value={period}
                      data={periodMapData}
                      onChange={(e) => handlePeriodChange('period', e)}
                    />
                  </>
                )}
            </Flex>
          </Drawer>
        </Fragment>
      )}
    </div>
  );
};

export default FilterMyReports;
